import { Injectable, InjectionToken, Provider } from '@angular/core';
import { ApiContactValidDateRangeResponse } from '../api-types/contact.api';
import {
	ApiAssignmentMoveValidDateRangeResponse,
	ApiDateRange,
	ApiValidationPhaseResponse,
} from '../api-types/validation.api';
import { ContactValidDateRange } from '../app-types/contact.app';
import { AssignmentMoveValidDateRangeResponse, DateRange, ValidationPhaseResponse } from '../app-types/validation.app';
import { transformApiContactDateRangeToAppContactDateRange } from '../transformers/contact.transform';
import {
	transformApiAssignmentMoveValidDateRangeToAppAssignmentMoveValidDateRange,
	transformApiDateRangeToAppDateRange,
	transformApiValidationPhaseResponseToApp,
} from '../transformers/validation.transform';
import { BaseRequestService } from './base-request.service';

export const VALIDATION_SERVICE = new InjectionToken<ValidationApi>('ValidationApi');

export interface ValidationApi {
	validatePhaseTransition(
		clientId: number | string,
		assignmentId: number | string,
		fromPhase: string | number,
		toPhase: string | number,
	): Promise<ValidationPhaseResponse>;
	validateServiceOfficeStatementPhaseTransition(
		serviceOfficeStatementId: number | string,
		clientId: number | string,
		toPhase: string | number,
	): Promise<ValidationPhaseResponse>;
	validateAdditionalAssignmentPhaseTransition(
		assignmentId: number | string,
		clientId: number | string,
		toPhase: string | number,
	): Promise<ValidationPhaseResponse>;
	getValidDateRangeForContactOfAssignment(
		clientId: number | string,
		assignmentId: number | string,
	): Promise<ContactValidDateRange>;
	validateMoveAction(userId: number | string): Promise<boolean>;
	validateMoveDates(assignmentId: number | string): Promise<AssignmentMoveValidDateRangeResponse>;
	validateCanReceiveRepresentation(userId: number | string): Promise<boolean>;
	getValidClarificationDateRange(assignmentId: number): Promise<DateRange>;
	getValidDateRangeForLEKOfAssignment(assignmentId: number | string, lekId?: number | string): Promise<DateRange>;
}

@Injectable()
export class ValidationService extends BaseRequestService implements ValidationApi {
	public validatePhaseTransition = async (
		clientId: number | string,
		assignmentId: number | string,
		fromPhase: string | number,
		toPhase: string | number,
	): Promise<ValidationPhaseResponse> => {
		const response = await this.get<ApiValidationPhaseResponse>(
			`validation/clients/${clientId}/assignments/${assignmentId}/canTransferToPhase/fromPhase/${fromPhase}/toPhase/${toPhase}`,
		);
		const result = transformApiValidationPhaseResponseToApp(response);
		return result;
	};

	public validateServiceOfficeStatementPhaseTransition = async (
		serviceOfficeStatementId: number | string,
		clientId: number | string,
		toPhase: string | number,
	): Promise<ValidationPhaseResponse> => {
		const response = await this.get<ApiValidationPhaseResponse>(
			`validation/serviceOfficeStatement/${serviceOfficeStatementId}/clients/${clientId}/canTransferToPhase/${toPhase}`,
		);
		const result = transformApiValidationPhaseResponseToApp(response);
		return result;
	};

	public validateAdditionalAssignmentPhaseTransition = async (
		assignmentId: number | string,
		clientId: number | string,
		toPhase: string | number,
	): Promise<ValidationPhaseResponse> => {
		const response = await this.get<ApiValidationPhaseResponse>(
			`validation/additionalAssignments/clients/${clientId}/assignments/${assignmentId}/canTransferToPhase/${toPhase}`,
		);
		const result = transformApiValidationPhaseResponseToApp(response);
		return result;
	};

	public getValidDateRangeForContactOfAssignment = async (
		clientId: number | string,
		assignmentId: number | string,
	): Promise<ContactValidDateRange> => {
		const response = await this.get<ApiContactValidDateRangeResponse>(
			`validation/contacts/clients/${clientId}/assignments/${assignmentId}`,
		);
		const result = transformApiContactDateRangeToAppContactDateRange(response);
		return result;
	};

	public validateMoveAction = async (userId: number | string) => {
		return this.get<boolean>(`validation/users/${userId}/moveAction`);
	};

	public validateMoveDates = async (assignmentId: number | string) => {
		const response = await this.get<ApiAssignmentMoveValidDateRangeResponse>(
			`validation/assignments/${assignmentId}/movedates`,
		);
		return transformApiAssignmentMoveValidDateRangeToAppAssignmentMoveValidDateRange(response);
	};

	public validateCanReceiveRepresentation = async (userId: number | string) => {
		return await this.get<boolean>(`validation/users/${userId}/canReceiveRepresentation`);
	};

	public getValidClarificationDateRange = async (assignmentId: number) => {
		const response = await this.get<ApiDateRange>(`validation/assignments/${assignmentId}/clarification`);
		return transformApiDateRangeToAppDateRange(response);
	};

	public getValidDateRangeForLEKOfAssignment = async (assignmentId: number | string, lekId?: number | string) => {
		const response = await this.get<ApiDateRange>(
			`validation/assignments/${assignmentId}/assistanceResult`,
			lekId !== undefined ? { assistanceResultId: lekId } : undefined,
		);
		return transformApiDateRangeToAppDateRange(response);
	};
}

export const provideValidationApi: Provider = {
	provide: VALIDATION_SERVICE,
	useClass: ValidationService,
};
